<template>
    <b-card no-body class="declaration-approval-form">
        <b-card-body>
            <h4 class="mb-50">{{ translate(
                'Page(ReceivedRequest):Component(ReviewAndSubmit):confirm-identity',
                'Confirm your identity',
            ) }}</h4>
            <p class="text-less-muted mb-2">{{ translate(
                'Page(ReceivedRequest):Component(ReviewAndSubmit):confirm-identity-description',
                'A verification code will be sent to your email address before the declaration can be submitted.',
            ) }}</p>

            <b-alert v-if="approved" variant="success" show class="mb-0">
                <div class="alert-body">
                    {{ translate(
                        'Page(ReceivedRequest):Component(ReviewAndSubmit):identity-confirmed',
                        'Identity confirmed for this organisation.',
                    ) }}
                </div>
            </b-alert>

            <template v-else>
                <b-row class="mb-1">
                    <b-col cols="12" md="4" class="mb-1 mb-md-0">
                        <b-form-group
                            class="mb-0"
                            :label="translate(
                                'Page(ReceivedRequest):Component(Label):name',
                                'Name',
                            )"
                        >
                            <b-form-input
                                v-model="name"
                                :disabled="busy"
                                :state="showIdentityErrors ? nameValid : null"
                                :placeholder="translate(
                                    'Page(ReceivedRequest):Component(Placeholder):name',
                                    'Name',
                                )"
                                @input="emitState"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-1 mb-md-0">
                        <b-form-group
                            class="mb-0"
                            :label="translate(
                                'Component(Label):email',
                                'Email',
                            )"
                        >
                            <b-form-input
                                v-model="email"
                                type="email"
                                :disabled="busy"
                                :state="showIdentityErrors ? emailValid : null"
                                :placeholder="translate(
                                    'Component(Placeholder):email',
                                    'Email',
                                )"
                                @input="emitState"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="4" class="d-flex align-items-end">
                        <b-button
                            variant="outline-primary"
                            block
                            :disabled="busy || resendSeconds > 0"
                            @click="requestCode"
                        >
                            <b-spinner v-if="requestingCode" small class="mr-50" />
                            {{ codeSent
                                ? translate('Component(Button):resend-code', 'Resend code')
                                : translate('Component(Button):send-code', 'Send verification code') }}
                            <span v-if="resendSeconds > 0">({{ resendSeconds }})</span>
                        </b-button>
                    </b-col>
                </b-row>

                <b-row v-if="codeSent">
                    <b-col cols="12" md="4" offset-md="4">
                        <b-form-group
                            class="mb-50"
                            :label="translate(
                                'Component(Label):code',
                                'Code',
                            )"
                        >
                            <b-input-group>
                                <b-form-input
                                    v-model="code"
                                    inputmode="numeric"
                                    autocomplete="one-time-code"
                                    :disabled="busy"
                                    :state="verificationError ? false : null"
                                    :placeholder="translate(
                                        'Component(Placeholder):code',
                                        'Code',
                                    )"
                                    @input="onCodeInput"
                                    @keyup.enter="verifyCode"
                                />
                                <b-input-group-append>
                                    <b-button
                                        variant="primary"
                                        :disabled="busy || code.trim().length === 0"
                                        @click="verifyCode"
                                    >
                                        <b-spinner v-if="verifying" small class="mr-50" />
                                        {{ translate('Component(Button):verify', 'Verify code') }}
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                            <small v-if="verificationError" class="text-danger">
                                {{ verificationError }}
                            </small>
                        </b-form-group>

                        <div class="declaration-approval-form__status text-less-muted">
                            <span>{{ translate(
                                'Page(ReceivedRequest):Component(ReviewAndSubmit):code-sent',
                                'A verification code has been sent.',
                            ) }}</span>
                            <span>{{ verifying
                                ? translate('Page(ReceivedRequest):Component(ReviewAndSubmit):verifying', 'Verifying...')
                                : translate('Page(ReceivedRequest):Component(ReviewAndSubmit):not-verified', 'Not yet verified') }}</span>
                        </div>
                    </b-col>
                </b-row>
            </template>
        </b-card-body>
    </b-card>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BRow,
    BSpinner,
} from 'bootstrap-vue'

const RESEND_SECONDS = 60

export default {
    name: 'DeclarationApprovalForm',
    components: {
        BAlert,
        BButton,
        BCard,
        BCardBody,
        BCol,
        BFormGroup,
        BFormInput,
        BInputGroup,
        BInputGroupAppend,
        BRow,
        BSpinner,
    },
    props: {
        approval: { type: Object, required: true },
        verifying: { type: Boolean, default: false },
    },
    data() {
        const identity = this.approval.identity || {}
        return {
            name: identity.name || '',
            email: identity.email || '',
            code: '',
            codeSent: false,
            requestingCode: false,
            resendSeconds: 0,
            resendTimer: null,
            showIdentityErrors: false,
            verificationError: null,
        }
    },
    computed: {
        approved() {
            return this.approval.approved === true
        },
        busy() {
            return this.requestingCode || this.verifying
        },
        nameValid() {
            return this.name.trim().length > 0
        },
        emailValid() {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim())
        },
        ready() {
            return this.approved
                || (this.nameValid && this.emailValid && this.codeSent && this.code.trim().length > 0)
        },
    },
    watch: {
        approved: {
            immediate: true,
            handler() {
                this.$nextTick(this.emitState)
            },
        },
    },
    beforeDestroy() {
        clearTimeout(this.resendTimer)
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        emitState() {
            this.$emit('state-change', {
                name: this.name.trim(),
                email: this.email.trim(),
                code: this.code.trim(),
                ready: this.ready,
            })
        },
        onCodeInput() {
            this.verificationError = null
            this.emitState()
        },
        verifyCode() {
            if (this.busy || this.code.trim().length === 0) {
                return
            }

            this.verificationError = null
            this.$emit('verify-code', this.code.trim())
        },
        requestCode() {
            this.showIdentityErrors = true
            this.verificationError = null
            if (!this.nameValid || !this.emailValid || this.requestingCode || this.resendSeconds > 0) {
                return
            }

            this.requestingCode = true
            this.$emit('request-code', {
                name: this.name.trim(),
                email: this.email.trim(),
                completionCallback: () => {
                    this.requestingCode = false
                    this.codeSent = true
                    this.resendSeconds = RESEND_SECONDS
                    this.tickResendTimer()
                    this.emitState()
                },
                failureCallback: error => {
                    this.requestingCode = false
                    this.verificationError = this.errorMessage(error)
                    this.emitState()
                },
            })
        },
        tickResendTimer() {
            clearTimeout(this.resendTimer)
            if (this.resendSeconds <= 0) {
                return
            }
            this.resendTimer = setTimeout(() => {
                this.resendSeconds -= 1
                this.tickResendTimer()
            }, 1000)
        },
        setVerificationError(error) {
            this.verificationError = this.errorMessage(error)
        },
        errorMessage(error) {
            return error && error.response && error.response.data && error.response.data.detail
                ? error.response.data.detail
                : (error && error.message ? error.message : String(error || 'Verification failed.'))
        },
    },
}
</script>

<style lang="scss" scoped>
.declaration-approval-form {
    border-color: #ebe9f1;

    &__status {
        display: flex;
        justify-content: space-between;
    }
}
</style>
